import { ArrowBack, ContactSupport, DriveFileRenameOutline, Key, Logout, Notifications } from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'
import { AppBar, Avatar, Badge, Box, Chip, Divider, IconButton, InputBase, Menu, MenuItem, Toolbar, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../store/hooks'
import { clearAuth } from '../store'
import { setCredentials } from '../store/reducers/auth'
import { resetMedia } from '../store/reducers/media'
import { resetPlaylist } from '../store/reducers/playlist'
import { resetPriceTable } from '../store/reducers/priceTable'
import { resetProduct } from '../store/reducers/product'
import { resetTemplate } from '../store/reducers/template'
import { clearPageTitle, setPageTitle } from '../store/reducers/ui'
import { resetUsers } from '../store/reducers/user'
import { toSentenceCase } from '../Util/stringUtils'
import ModalChangePassword from './Modal/ModalChangePassword'
import ModalSupport from './Modal/ModalSupport'

type Props = {
  open: boolean
  isEditMode?: boolean
  onMenuClick?: () => void
  backLabel?: string
  backPath?: string
}

const UserBar = ({ open, isEditMode = false, onMenuClick, backLabel = 'Playlists', backPath = '/playlists' }: Props) => {
  const theme = useTheme()
  const isDark = false
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const auth = useAppSelector(state => state.auth)
  const pageTitle = useAppSelector(state => state.ui.pageTitle)
  const [openModal, setOpenModal] = useState(false)
  const [openSupport, setOpenSupport] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [showPencil, setShowPencil] = useState(false)
  const [localTitle, setLocalTitle] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const barBg    = isDark ? '#111c22' : '#f8fafb'
  const barBdr   = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)'
  const notifBg  = isDark ? '#152028' : '#ffffff'
  const textMuted = isDark ? '#4a6a78' : '#8aaabb'
  const teal = isDark ? '#14b8a0' : '#0d9488'

  useEffect(() => { setLocalTitle(pageTitle) }, [pageTitle])
  useEffect(() => { if (isEditingTitle) inputRef.current?.focus() }, [isEditingTitle])

  function handleTitleConfirm() { setIsEditingTitle(false); setShowPencil(false); dispatch(setPageTitle(toSentenceCase(localTitle))) }
  function handleBack() { dispatch(clearPageTitle()); navigate(backPath) }
  function handleLogOut() {
    clearAuth()
    dispatch(resetMedia()); dispatch(resetPlaylist()); dispatch(resetPriceTable())
    dispatch(resetProduct()); dispatch(resetTemplate()); dispatch(resetUsers())
    dispatch(setCredentials({ user: null, token: null }))
  }

  const todayStr = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <>
      <ModalChangePassword onClose={() => setOpenModal(false)} open={openModal} />
      <ModalSupport onClose={() => setOpenSupport(false)} open={openSupport} />

      <AppBar position="static" elevation={0} sx={{
        bgcolor: barBg,
        borderBottom: `1px solid ${barBdr}`,
        boxShadow: 'none',
        zIndex: theme.zIndex.drawer - 1,
      }}>
        <Toolbar disableGutters sx={{ minHeight: '56px !important', px: 2, display: 'flex', alignItems: 'center', position: 'relative' }}>

          {isEditMode ? (
            <>
              {/* Voltar */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, zIndex: 1 }}>
                <IconButton size="small" onClick={handleBack} sx={{ color: 'text.secondary', mr: 0.5 }}>
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Typography variant="body2" color="text.secondary" onClick={handleBack}
                  sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }}>
                  {backLabel}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />

              {/* Título centralizado */}
              <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                {isEditingTitle ? (
                  <InputBase inputRef={inputRef} value={localTitle}
                    onChange={e => setLocalTitle(e.target.value)}
                    onBlur={handleTitleConfirm}
                    onKeyDown={e => { if (e.key === 'Enter') handleTitleConfirm() }}
                    sx={{
                      fontSize: '1rem', fontWeight: 600, color: 'text.primary',
                      '& input': { textAlign: 'center', p: 0.5 },
                      borderBottom: `1.5px solid ${teal}`, minWidth: 200,
                    }} />
                ) : (
                  <Box onMouseEnter={() => setShowPencil(true)} onMouseLeave={() => setShowPencil(false)}
                    onClick={() => setIsEditingTitle(true)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'text', px: 1.5, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ color: 'text.primary' }}>
                      {pageTitle || 'Nova Playlist'}
                    </Typography>
                    <DriveFileRenameOutline sx={{ fontSize: 15, color: 'text.secondary', opacity: showPencil ? 1 : 0, transition: 'opacity 0.15s' }} />
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <>
              {/* Mobile menu button */}
              {isMobile && (
                <IconButton onClick={onMenuClick} size="small" sx={{ mr: 1 }}>
                  <MenuIcon />
                </IconButton>
              )}

              {/* Saudação + data */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.4px', lineHeight: 1.2 }}>
                  Olá, {auth.user?.name?.split(' ')[0] ?? 'usuário'}!
                  {!isMobile && (
                    <Box component="span" sx={{ color: textMuted, fontWeight: 400, fontSize: '0.875rem', ml: 1.5 }}>
                      | {todayStr.charAt(0).toUpperCase() + todayStr.slice(1)}
                    </Box>
                  )}
                </Typography>
              </Box>
            </>
          )}

          {/* Direita: sino + avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            {/* Sino */}
            <IconButton size="small" sx={{
              width: 36, height: 36, borderRadius: '50%',
              bgcolor: notifBg, border: `1px solid ${barBdr}`,
            }}>
              <Badge color="error" variant="dot">
                <Notifications sx={{ fontSize: 18, color: textMuted }} />
              </Badge>
            </IconButton>

            {/* Avatar + menu */}
            <Tooltip title="Configurações">
              <IconButton onClick={e => setAnchorElUser(e.currentTarget)} sx={{ p: 0 }}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: teal, fontSize: 14, fontWeight: 700 }}>
                  {auth.user?.name?.charAt(0).toUpperCase() ?? 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu sx={{ mt: '44px' }} anchorEl={anchorElUser}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorElUser)} onClose={() => setAnchorElUser(null)}>
              <MenuItem onClick={() => { setOpenModal(true); setAnchorElUser(null) }}>
                <Key sx={{ mr: 1, fontSize: 18 }} /><Typography fontSize={14} fontWeight={500}>Mudar Senha</Typography>
              </MenuItem>
              <MenuItem onClick={() => { setOpenSupport(true); setAnchorElUser(null) }}>
                <ContactSupport sx={{ mr: 1, fontSize: 18 }} /><Typography fontSize={14} fontWeight={500}>Suporte</Typography>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleLogOut}>
                <Logout sx={{ mr: 1, fontSize: 18, color: 'error.main' }} />
                <Typography fontSize={14} fontWeight={500} color="error.main">Sair</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  )
}

export default UserBar
